import { useState } from 'react';
import { Copy, MessageCircle, Mail, X } from 'lucide-react';

import { useToast } from '../../components/Toast';
import { mailtoHref, whatsappHref } from '../../lib/quickActions';
import { buildShareUrl } from './quote.schema';
import styles from './quotes.module.css';

interface Props {
  open: boolean;
  quoteId: string;
  quoteNumber: string;
  customerName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  onClose: () => void;
}

export function QuoteShareSheet({
  open,
  quoteId,
  quoteNumber,
  customerName,
  contactPhone,
  contactEmail,
  onClose,
}: Props) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const url = buildShareUrl(quoteId);
  const greeting = customerName ? `שלום ${customerName},` : 'שלום,';
  const messageBody = `${greeting}\nהנה הצעת המחיר ${quoteNumber}:\n${url}`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('הקישור הועתק');
      window.setTimeout(() => { setCopied(false); }, 2000);
    } catch {
      toast.error('לא הצלחנו להעתיק');
    }
  }

  const whatsapp = contactPhone ? whatsappHref(contactPhone) : null;
  const mail = contactEmail ? mailtoHref(contactEmail) : null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className={styles.section}
        style={{ maxWidth: 480, width: '90%' }}
        onClick={(e) => { e.stopPropagation(); }}
      >
        <div className={styles.drawerHead}>
          <h2 className={styles.drawerTitle}>שתף הצעת מחיר</h2>
          <button type="button" className={styles.btnGhost} onClick={onClose} aria-label="סגור">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className={styles.shareSheet}>
          <div>
            <span className={styles.label}>קישור ציבורי</span>
            <div className={styles.shareUrl}><bdi dir="ltr">{url}</bdi></div>
          </div>

          <div className={styles.shareButtons}>
            <button type="button" className={styles.btnSecondary} onClick={() => { void handleCopy(); }}>
              <Copy size={16} aria-hidden="true" />
              {copied ? 'הועתק' : 'העתק קישור'}
            </button>

            {whatsapp ? (
              <a
                className={styles.btnSecondary}
                href={`${whatsapp}?text=${encodeURIComponent(messageBody)}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                <MessageCircle size={16} aria-hidden="true" />
                שלח בוואטסאפ
              </a>
            ) : null}

            {mail ? (
              <a
                className={styles.btnSecondary}
                href={`${mail}?subject=${encodeURIComponent(`הצעת מחיר ${quoteNumber}`)}&body=${encodeURIComponent(messageBody)}`}
              >
                <Mail size={16} aria-hidden="true" />
                שלח באימייל
              </a>
            ) : null}
          </div>

          {!whatsapp && !mail ? (
            <p className={styles.notice}>אין טלפון או אימייל לאיש קשר ראשי — שתף את הקישור ידנית.</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
